import * as React from 'react';
import { CLASS_BOARD_DETAILS, CLASS_BOARD_DETAILS_SECTION } from '../../../../constants';
import { SettingsSection } from '../../../../../../base/components/SettingsSection/SettingsSection';
import { IWebhooksSectionProps } from './types';
import './_WebhooksSection.scss';
import { WEBHOOKS_SECTION_TITLE } from '../../constants';
import { Input, Loader } from 'kui';
import {
    WEBHOOKS_SECTION_ERROR_RESPONSE, WEBHOOKS_SECTION_ERROR_STATUS,
    WEBHOOKS_SECTION_ERROR_URL,
    WEBHOOKS_SECTION_PLACEHOLDER,
    WEBHOOKS_SECTION_TEXT
} from './constants';
import { ErrorHint } from '../../../../../../base/components/ErrorHint/ErrorHint';
import { v4 as uuidv4 } from 'uuid';
import { TStatus } from '../../../../../../../../types/model';

export function WebhooksSection ({
    isReadonly,
    isShow,
    webhook,
    onDelete,
    onDidMount,
    onSave,
}: IWebhooksSectionProps) {
    if (!isShow) return null;

    const className = CLASS_BOARD_DETAILS + '-webhooks';

    const [value, setValue] = React.useState(webhook && webhook.webhookUrl || '');
    const [isLoading, setLoading] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [errorKey, setErrorKey] = React.useState(uuidv4());

    const onChange = (
        e: React.SyntheticEvent
    ) => {
        const target = e.target as HTMLInputElement;
        const { value } = target;
        setError(null);
        setValue(value);
    };

    const onTryToSave = () => {
        const newWebhook = value.trim();
        if (webhook && newWebhook === webhook.webhookUrl) return;

        if (!newWebhook) {
            onDelete();
        } else {
            let isValidUrl;
            try {
                new URL(newWebhook);
                isValidUrl = true;
            } catch (e) {
                isValidUrl = false;
            }
            if (isValidUrl) {
                setLoading(true);
                onSave(newWebhook).then(isValid => {
                    setLoading(false);
                    if (!isValid) {
                        setError(WEBHOOKS_SECTION_ERROR_RESPONSE);
                    }
                });
            } else {
                setError(WEBHOOKS_SECTION_ERROR_URL);
            }
        }
    };

    const onEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onTryToSave();
        }
    };

    React.useEffect(() => {
        if (!webhook) {
            setLoading(true);
            onDidMount().finally(() => {
                setLoading(false);
            });
        } else {
            setValue(webhook.webhookUrl);
            if (webhook.status === TStatus.STATUS_ERROR) {
                setError(WEBHOOKS_SECTION_ERROR_STATUS);
            }
        }
    }, [webhook]);

    React.useEffect(() => {
        setErrorKey(uuidv4());
    }, [error]);

    return (
        <SettingsSection
            className={`
                ${CLASS_BOARD_DETAILS_SECTION}
                ${CLASS_BOARD_DETAILS_SECTION}--webhooks
                ${className}
            `}
            title={WEBHOOKS_SECTION_TITLE}
        >
            <p className={className + '__text'}>{WEBHOOKS_SECTION_TEXT}</p>
            <div className={className + '__input-wrapper'}>
                <Input
                    value={value}
                    onChange={onChange}
                    state={error ? 'error' : null}
                    onBlur={onTryToSave}
                    onKeyDown={onEnter}
                    disabled={isLoading || isReadonly}
                    placeholder={WEBHOOKS_SECTION_PLACEHOLDER}
                />
                <ErrorHint
                    key={errorKey}
                    direction={'up'}
                    portalSelector={`.${className} .kui-label`}
                    value={error}
                    selector={`.${className} .kui-input__item`}
                />
                {isLoading && <div className={className + '__loader'}><Loader /></div>}
            </div>
        </SettingsSection>
    );
};
